import { FormHelperText, Grid, TextField } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcContainer, MprcFormSubHeader, MprcStringToHtml } from "components";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedQuestionResponse, QuestionType, TagUsage } from "generated/CoreApi_v1";
import { useSession } from "providers/SessionProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { useQuestionContext } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";
import { EditQuestionAvailableQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionAvailableQuestion";
import { TagAutoComplete } from "views/foundation/tag/components/TagAutoComplete";

////////////////////////////////////////////////////////////////////////////////

export interface IPostValidationPointOther {
    points: string[] | undefined;
    isBlocked: string[] | undefined;
    allowInKnowledgeTests: string[] | undefined;
}

export interface IValidationFieldsPointOthers {
    points: boolean;
}

export function validatePointOther(dto: ManagedQuestionResponse): IValidationFieldsPointOthers {
    return {
        points: dto.evaluation?.points != undefined && dto.evaluation.points > 0 && dto.evaluation.points <= 100,
    };
}

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    onError: (error: any) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditQuestionPointOthers: React.FC<IPros> = (props) => {

    const { permissionExplorer } = useSession();
    const { t } = useTranslation("assessment");

    const { onError } = props;
    const { viewData, postValidation, validationFields, readonly, editMode, handleUpdateRecord, handleUpdateEvaluation } = useQuestionContext();

    const evaluation = viewData.evaluation!;
    const currentPostValidation = postValidation.pointOtherBox;
    const currentvalidationFields = validationFields.pointOtherBox;

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {
                editMode == "default" &&
                <MprcContainer>
                    <Grid container>
                        <Grid size="grow">
                            <MprcFormSubHeader
                                title={t("Question.Edit.PointOthers.PointLabel")}
                                subtitle={t("Question.Edit.PointOthers.PointHint")}
                            />
                            <TextField
                                fullWidth
                                name="points"
                                type="number"
                                autoComplete="off"
                                margin="dense"
                                disabled={readonly}
                                value={evaluation.points || ""}
                                onChange={(e) => handleUpdateEvaluation("points", e.target.value)}
                                sx={{ width: 100 }}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            max: 100,
                                            min: 0
                                        }
                                    }
                                }}
                                error={(evaluation.points != null && !currentvalidationFields.points) || !!currentPostValidation.points}
                                helperText={currentPostValidation?.points && <MprcStringToHtml text={currentPostValidation?.points} />}
                            />
                        </Grid>
                    </Grid>
                </MprcContainer>
            }
            <>
                {
                    viewData.questionId == null && viewData.exclusiveAssessmentId && permissionExplorer.hasOne([CorePermissions.AssessmentManageQuestionBank])
                        ? <EditQuestionAvailableQuestion onError={onError} />
                        :
                        <>
                            {
                                (viewData.exclusiveAssessmentId == undefined || viewData.questionGroupId != undefined) &&
                                <MprcContainer>
                                    <MprcFormSubHeader
                                        title={t("Question.Edit.PointOthers.AvailabilityTitle")}
                                    />
                                    {
                                        viewData.exclusiveAssessmentId == undefined &&
                                        <>
                                            <MprcSwitch
                                                label={t("Question.Edit.PointOthers.IsBlockedLabel")}
                                                checked={viewData.isBlocked ?? false}
                                                disabled={readonly}
                                                onChange={(e) => handleUpdateRecord("isBlocked", e.target.checked)}
                                            />
                                            {
                                                !!currentPostValidation.isBlocked &&
                                                <FormHelperText error>{<MprcStringToHtml text={currentPostValidation.isBlocked} />}</FormHelperText>
                                            }
                                        </>
                                    }
                                    {
                                        viewData.questionGroupId != undefined &&
                                        (viewData.questionType == QuestionType.SimpleChoice || viewData.questionType == QuestionType.MultipleChoice) &&
                                        <>
                                            <MprcSwitch
                                                label={t("Question.Edit.PointOthers.AllowInKnowledgeTestsLabel")}
                                                checked={evaluation.allowInKnowledgeTests}
                                                onChange={(e) => handleUpdateEvaluation("allowInKnowledgeTests", e.target.checked)}
                                                disabled={readonly || editMode == "challenge"}
                                                helperText={t("Question.Edit.PointOthers.AllowInKnowledgeTestsHint")}
                                            />
                                            {
                                                !!currentPostValidation.allowInKnowledgeTests &&
                                                <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.allowInKnowledgeTests} />}</FormHelperText>
                                            }
                                        </>
                                    }
                                </MprcContainer>
                            }
                        </>
                }
            </>
            {
                viewData.availableQuestioBank != true &&
                <>
                    <MprcFormSubHeader
                        title={t("Question.Edit.PointOthers.TagsLabel")}
                    />
                    <TagAutoComplete
                        multiSelect
                        selectedItems={viewData.tags}
                        usage={TagUsage.Learning}
                        disabled={readonly}
                        canCreate
                        onChange={(items) => handleUpdateRecord("tags", items)}
                        onError={error => onError(error)}
                    />
                </>
            }
        </>
    );
};
